import { useState, useEffect } from 'react'
import { NavLink, Link } from 'react-router-dom'
import { Menu, X, Bell } from 'lucide-react'

const navLinks = [
  { to: '/',             label: 'Home',         end: true },
  // { to: '/chi-siamo',    label: 'Chi Siamo' },
  { to: '/servizi',      label: 'Servizi' },
  { to: '/eventi',       label: 'Eventi' },
  // { to: '/contatti',     label: 'Contatti' },
  { to: '/safeguarding',        label: 'Safeguarding' },
  { to: '/contributi-pubblici', label: 'Contributi' },
  { to: '/donazioni',           label: 'Donazioni' },

]

interface NavbarProps {
  onOpenPopup?: () => void
}

export default function Navbar({ onOpenPopup }: NavbarProps) {
  const [menuOpen, setMenuOpen] = useState(false)
  const [scrolled, setScrolled] = useState(false)

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40)
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  useEffect(() => {
    const onResize = () => { if (window.innerWidth >= 768) setMenuOpen(false) }
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [])

  useEffect(() => {
    document.body.style.overflow = menuOpen ? 'hidden' : ''
    return () => { document.body.style.overflow = '' }
  }, [menuOpen])

  return (
    <>
      <header
        style={scrolled ? { backgroundColor: 'var(--navbar-bg)' } : undefined}
        className={`
          fixed top-0 left-0 right-0 z-50
          transition-all duration-500
          ${scrolled
            ? 'backdrop-blur-md border-b border-themed py-3 shadow-[0_2px_20px_rgba(0,0,0,0.15)]'
            : 'bg-transparent py-5'
          }
        `}
      >
        <div className="max-w-7xl mx-auto px-4 md:px-8 flex items-center justify-between">

          {/* Logo */}
          <Link
  to="/"
  className="flex items-center gap-2 group" // Usa gap-1 o gap-2 per tenerli vicini
  onClick={() => setMenuOpen(false)}
>
  <img
    src="/images/logo1.png"
    alt="Logo 1"
    className="h-10 w-auto object-contain transition-opacity duration-200 group-hover:opacity-80"
  />
  <img
    src="/images/logo2.png"
    alt="Logo 2"
    className="h-10 w-auto object-contain transition-opacity duration-200 group-hover:opacity-80"
  />
</Link>


          {/* Nav desktop */}
          <nav className="hidden md:flex items-center gap-1">
            {navLinks.map(({ to, label, end }) => (
              <NavLink
                key={to}
                to={to}
                end={end}
                className={({ isActive }) => `
                  relative px-4 py-2 text-sm font-sans font-medium tracking-wide
                  transition-colors duration-200
                  after:absolute after:bottom-0 after:left-1/2 after:-translate-x-1/2
                  after:h-px after:bg-gold-500 after:transition-all after:duration-300
                  ${isActive ? 'text-gold-400 after:w-4' : 'hover:text-gold-400 after:w-0 hover:after:w-4'}
                `}
                style={({ isActive }) =>
                  isActive ? undefined : { color: 'var(--text-primary)' }
                }
              >
                {label}
              </NavLink>
            ))}

            <Link
              to="/contatti"
              className="
                ml-4 px-5 py-2 text-sm font-sans font-medium tracking-wide
                border border-gold-500 text-gold-400 rounded-sm
                hover:bg-gold-500 hover:text-board-dark
                transition-all duration-200
              "
            >
              Contattaci
            </Link>

            {/* Tasto notifica desktop */}
            <button
              onClick={onOpenPopup}
              className="relative ml-2 p-2 rounded-full transition-colors duration-200"
              style={{ color: 'var(--text-primary)' }}
              onMouseOver={e => (e.currentTarget.style.color = '#d4af37')}
              onMouseOut={e => (e.currentTarget.style.color = 'var(--text-primary)')}
              aria-label="Avvisi"
            >
              <Bell size={18} />
              <span
                className="absolute top-1 right-1 block w-2 h-2 rounded-full"
                style={{ backgroundColor: '#d4af37' }}
              />
            </button>

          </nav>

          {/* Hamburger mobile + tasto notifica */}
          <div className="md:hidden flex items-center gap-1">
            {/* Tasto notifica — a sinistra delle 3 lineette */}
            <button
              onClick={onOpenPopup}
              className="relative p-2 transition-colors duration-200"
              style={{ color: 'var(--text-primary)' }}
              onMouseOver={e => (e.currentTarget.style.color = '#d4af37')}
              onMouseOut={e => (e.currentTarget.style.color = 'var(--text-primary)')}
              aria-label="Avvisi"
            >
              <Bell size={20} />
              <span
                className="absolute top-1.5 right-1.5 block w-2 h-2 rounded-full"
                style={{ backgroundColor: '#d4af37' }}
              />
            </button>

            <button
              className="p-2 hover:text-gold-400 transition-colors"
              style={{ color: 'var(--text-primary)' }}
              onClick={() => setMenuOpen(v => !v)}
              aria-label={menuOpen ? 'Chiudi menu' : 'Apri menu'}
            >
              {menuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </header>

      {/* Mobile menu overlay */}
      <div
        style={{ backgroundColor: 'var(--bg-primary)' }}
        className={`
          fixed inset-0 z-40 flex flex-col
          transition-all duration-300
          md:hidden
          ${menuOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}
        `}
      >
        <div className="flex-1 flex flex-col justify-center items-center gap-2 px-8">
          {navLinks.map(({ to, label, end }, i) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              onClick={() => setMenuOpen(false)}
              style={({ isActive }) => ({
                transitionDelay: menuOpen ? `${i * 60}ms` : '0ms',
                color: isActive ? undefined : 'var(--text-primary)',
              })}
              className={({ isActive }) => `
                text-2xl font-serif tracking-wide py-3
                transition-all duration-300
                ${menuOpen ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}
                ${isActive ? 'text-gold-400' : 'hover:text-gold-400'}
              `}
            >
              {label}
            </NavLink>
          ))}

          <Link
            to="/contatti"
            onClick={() => setMenuOpen(false)}
            className="
              mt-6 px-8 py-3 text-base font-sans font-medium tracking-wide
              border border-gold-500 text-gold-400 rounded-sm
              hover:bg-gold-500 hover:text-board-dark
              transition-all duration-200
            "
          >
            Contattaci
          </Link>
        </div>

        {/* Decorazione mobile menu */}
        <div className="pb-10 text-center">
          <p
            className="text-xs font-sans tracking-widest uppercase"
            style={{ color: 'var(--text-muted)' }}
          >
            A Gonfie Vele — Scacchi
          </p>
        </div>
      </div>
    </>
  )
}